import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../store/auth.store';
import { disconnectSocket } from '../services/socket';
import { LoginPayload, RegisterPayload, UserRole } from '../types/api';

/**
 * Hooks Zustand grain-fin : chaque hook ne souscrit qu'à la slice qui l'intéresse,
 * pour éviter qu'un changement de `user` ne re-render un composant qui ne consomme
 * que `isAuthenticated` (et inversement). Les actions sont des références stables
 * en Zustand, donc pas besoin de useCallback côté store.
 */
export const useAuthUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useHasHydrated = () => useAuthStore((s) => s.hasHydrated);
export const useSetAuthSession = () => useAuthStore((s) => s.setSession);
export const useSetAuthUser = () => useAuthStore((s) => s.setUser);
export const useAuthLogoutAction = () => useAuthStore((s) => s.logout);

export const useHasRole = () => {
  const user = useAuthUser();
  return useCallback(
    (roles: UserRole[]) => {
      if (!user) return false;
      const userRoles: UserRole[] = user.roles ?? [];
      return roles.some((r) => userRoles.includes(r));
    },
    [user],
  );
};

/**
 * Hook composite — pratique pour les écrans qui ont vraiment besoin de tout
 * (login/register/profile). Pour la majorité des composants, préférer les hooks
 * grain-fin ci-dessus afin de minimiser les re-renders en cascade.
 */
export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const setSession = useSetAuthSession();
  const storeLogout = useAuthLogoutAction();
  const hasRole = useHasRole();

  const login = useCallback(
    async (payload: LoginPayload) => {
      const res = await authService.login(payload);
      setSession(res);
      return res;
    },
    [setSession],
  );

  const register = useCallback(
    async (payload: RegisterPayload) => {
      const res = await authService.register(payload);
      setSession(res);
      return res;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    await authService.logout();
    storeLogout();
    disconnectSocket();
    // Purge du cache : sur un poste partagé, l'utilisateur suivant voyait
    // pendant ~30 s les billets / wallet / créances du précédent.
    queryClient.clear();
    toast.success('Vous êtes déconnecté');
    navigate('/login', { replace: true });
  }, [storeLogout, navigate, queryClient]);

  return { user, isAuthenticated, login, register, logout, hasRole };
};
